import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns } from "../../utils/preferencesUtils";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const MENU_PRESET_SORT_FIELDS = {
  name: "Name",
  price: "Price",
  sortingOrder: "SortingOrder",
};

const MENU_PRESET_SORT_LABELS = {
  [MENU_PRESET_SORT_FIELDS.name]: "Name",
  [MENU_PRESET_SORT_FIELDS.price]: "Price",
  [MENU_PRESET_SORT_FIELDS.sortingOrder]: "Sorting Order",
};

const ManageMenuPresets = () => {
  const dispatch = useDispatch();

  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const compCurrency =
    useSelector((state) => state.auth.profileData?.compCurrency) || "GBP";

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState([]);
  const [statusConfirmMenu, setStatusConfirmMenu] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formStatus, setFormStatus] = useState(true);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const menusFetchSeqRef = useRef(0);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const justDraggedRef = useRef(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const currencySymbol = currencySymbols[compCurrency] || compCurrency;

  const pageKey = "menuPresets";
  const defaultVisibleColumns = {
    name: true,
    price: true,
    status: true,
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "Name", label: "Name" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: MENU_PRESET_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const hasFiltersApplied = () => search.trim() !== "";

  const fetchMenuPresets = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++menusFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MENU_PRESETS, {
        params: {
          CompID,
          PageNumber: pageNumber,
          PageSize: Number(pageSize.value),
          Search: search?.trim() || null,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== menusFetchSeqRef.current) return;

      const data = response.data.data || response.data || [];
      const list = Array.isArray(data) ? data : [];
      const records = response.data.totalRecords || list.length || 0;
      const pages = response.data.totalPages || 1;

      setMenus(list);
      setTotalRecords(records);
      setTotalPages(pages);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== menusFetchSeqRef.current) return;
      setMenus([]);
      setTotalRecords(0);
      setTotalPages(1);
      showErrorToast("Failed to load menu presets");
    } finally {
      if (fetchSeq === menusFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, pageNumber, pageSize, search, sortBy, sortOrder]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(menusFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useEffect(() => {
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useDebouncedEffect(() => fetchMenuPresets(), [fetchMenuPresets]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No menu presets match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no menu presets configured yet. Use Add Menu Preset to create one.";
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormName("");
    setFormPrice("");
    setFormStatus(true);
    setIsModalOpen(true);
  };

  const openEditModal = (menu) => {
    if (!menu) return;
    setEditingItem(menu);
    setFormName(menu.name ?? menu.Name ?? "");
    setFormPrice((menu.price ?? menu.Price ?? "").toString());
    setFormStatus(
      (typeof menu.status === "boolean" ? menu.status : null) ??
        (typeof menu.Status === "boolean" ? menu.Status : true)
    );
    setIsModalOpen(true);
  };

  const getMenuPresetStatus = (menu) =>
    typeof menu.status === "boolean"
      ? menu.status
      : typeof menu.Status === "boolean"
        ? menu.Status
        : true;

  const getMenuPresetName = (menu) => menu.name ?? menu.Name ?? "Menu preset";

  const confirmMenuPresetStatusChange = async () => {
    if (!statusConfirmMenu) return;

    const id = statusConfirmMenu.id ?? statusConfirmMenu.ID;
    if (!CompID || id == null) return;

    const currentStatus = getMenuPresetStatus(statusConfirmMenu);
    const newStatus = !currentStatus;

    setStatusConfirmMenu(null);

    try {
      await axiosInstance.post(APIs.UPDATE_MENU_PRESET, {
        ID: id,
        CompID,
        Name: statusConfirmMenu.name ?? statusConfirmMenu.Name ?? "",
        Price: Number(statusConfirmMenu.price ?? statusConfirmMenu.Price ?? 0),
        SortingOrder:
          statusConfirmMenu.sortingOrder ?? statusConfirmMenu.SortingOrder ?? 0,
        Status: newStatus,
      });
      showSuccessToast(
        `"${getMenuPresetName(statusConfirmMenu)}" is now ${newStatus ? "Active" : "InActive"}.`
      );
      fetchMenuPresets();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ??
          err.response?.data?.Message ??
          "Failed to update status"
      );
    }
  };

  const handleSave = async () => {
    if (!CompID) {
      showErrorToast("Company not found.");
      return;
    }

    if (!formName.trim()) {
      showErrorToast("Name is required.");
      return;
    }

    const priceVal = parseFloat(formPrice || "0");

    const payload = {
      ID: editingItem ? editingItem.id ?? editingItem.ID : 0,
      CompID,
      Name: formName.trim(),
      Price: priceVal,
      SortingOrder:
        editingItem && (editingItem.sortingOrder ?? editingItem.SortingOrder)
          ? editingItem.sortingOrder ?? editingItem.SortingOrder
          : 0,
      Status: formStatus,
    };

    try {
      setLoading(true);
      const api =
        editingItem && (editingItem.id ?? editingItem.ID)
          ? APIs.UPDATE_MENU_PRESET
          : APIs.ADD_MENU_PRESET;

      const response = await axiosInstance.post(api, payload);
      if (!response.data || response.data.status === false) {
        showErrorToast(response.data?.message || "Failed to save menu preset.");
      } else {
        showSuccessToast("Menu preset saved successfully.");
        setIsModalOpen(false);
        fetchMenuPresets();
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to save menu preset.");
    }
  };

  const handleDelete = async (menu) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_MENU_PRESET, {
        params: {
          ID: menu.id ?? menu.ID,
          CompID,
        },
      });

      if (response.data.status) {
        const deletedId = menu.id ?? menu.ID;
        setMenus((prev) => prev.filter((x) => (x.id ?? x.ID) !== deletedId));
        showSuccessToast("Menu preset deleted successfully");
      } else {
        showErrorToast(response.data.message || "Failed to delete menu preset");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to delete menu preset");
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from == null || to == null || from === to) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newMenus = [...menus];
    const dragged = newMenus[from];
    newMenus.splice(from, 1);
    newMenus.splice(to, 0, dragged);

    newMenus = newMenus.map((m, idx) => ({
      ...m,
      SortingOrder: idx + 1,
      sortingOrder: idx + 1,
    }));

    setMenus(newMenus);

    try {
      setLoading(true);
      await Promise.all(
        newMenus.map((m) =>
          axiosInstance.post(APIs.UPDATE_MENU_PRESET, {
            ID: m.id ?? m.ID,
            CompID,
            Name: m.name ?? m.Name,
            Price: m.price ?? m.Price ?? 0,
            SortingOrder: m.sortingOrder ?? m.SortingOrder ?? 0,
            Status: m.status ?? m.Status ?? true,
          })
        )
      );
      showSuccessToast("Menu preset sort order updated.");
    } catch (err) {
      showErrorToast("Failed to update sort order.");
      fetchMenuPresets();
    } finally {
      setLoading(false);
      dragItem.current = null;
      dragOverItem.current = null;
      document
        .querySelectorAll("tr")
        .forEach((tr) => (tr.style.backgroundColor = ""));
    }
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document
      .querySelectorAll("tr")
      .forEach((tr) => (tr.style.backgroundColor = ""));
    dragItem.current = null;
    dragOverItem.current = null;
    justDraggedRef.current = true;
  };

  const renderNoDataMessage = () => {
    if (loading || menus.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No menu presets found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Menu Presets</li>
          </ol>
          <h1 className="page-header mb-0">Menu Presets</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={openCreateModal}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Menu Preset
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <GridEmptyState
            isDarkMode={isDarkMode}
            themeColor={themeColor}
            title="No menu presets yet"
            description="Create your first menu preset to get started."
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Menu Presets"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                defaultColumns={defaultVisibleColumns}
                pageKey={pageKey}
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && menus.length > 0 && (
                  <div
                    className="table-responsive table-container mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "600px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={MENU_PRESET_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Menu Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.price && (
                            <SortableColumnHeader
                              sortField={MENU_PRESET_SORT_FIELDS.price}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Price
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {menus.map((menu, index) => (
                          <tr
                            key={menu.id ?? menu.ID}
                            draggable
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (justDraggedRef.current) {
                                justDraggedRef.current = false;
                                return;
                              }
                              openEditModal(menu);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openEditModal(menu);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                            onDragStart={(e) =>
                              handleDragStart(
                                e,
                                (pageNumber - 1) * Number(pageSize.value) + index
                              )
                            }
                            onDragEnter={(e) =>
                              handleDragEnter(
                                e,
                                (pageNumber - 1) * Number(pageSize.value) + index
                              )
                            }
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            {visibleColumns.name && (
                              <td className="align-middle">
                                {menu.name ?? menu.Name}
                              </td>
                            )}
                            {visibleColumns.price && (
                              <td className="align-middle text-center">
                                {currencySymbol}{" "}
                                {(() => {
                                  const p = menu.price ?? menu.Price;
                                  return typeof p === "number"
                                    ? p.toFixed(2)
                                    : p;
                                })()}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <StatusBadge
                                  isActive={getMenuPresetStatus(menu)}
                                  onClick={() => setStatusConfirmMenu(menu)}
                                />
                              </td>
                            )}
                            <td
                              className="align-middle text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TableActionsMenu>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setTimeout(() => openEditModal(menu), 0);
                                  }}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDelete(menu);
                                  }}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title={editingItem ? "Update Menu Preset" : "Add Menu Preset"}
        fontSize="18px"
        fontWeight={400}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        isCentered={true}
        showButton={false}
      >
        <div className="row g-3">
          <div className="col-md-12">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Price</label>
            <div className="input-group">
              <span className="input-group-text">{currencySymbol}</span>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <div className="d-flex align-items-center">
              <ToggleSwitch isChecked={formStatus} onToggle={setFormStatus} />
            </div>
          </div>
          <div className="col-12 d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        title="Change menu preset status?"
        subtitle={
          statusConfirmMenu
            ? `Are you sure you want to set "${getMenuPresetName(statusConfirmMenu)}" to ${
                getMenuPresetStatus(statusConfirmMenu) ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmMenu(null)}
        isOpen={!!statusConfirmMenu}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmMenuPresetStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageMenuPresets;
